import React, { useMemo, useState } from 'react';
import { Box, Typography, InputBase, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Search } from 'lucide-react';
import HostCard from './HostCard';
import GatewayCard from './GatewayCard';
import HostDetailDrawer from './HostDetailDrawer';

export default function ServerGrid({ hosts = [] }) {
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState('all');
    const [selected, setSelected] = useState(null);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return hosts.filter(h => {
            if (q) {
                const hay = `${h.name} ${h.host} ${h.ip || ''} ${h.kind || ''}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            if (filter === 'problems') {
                const hasProblem = (h.problems && h.problems.length > 0)
                    || !h.templateOk
                    || (h.metricCoverage && !h.metricCoverage.ok && h.metricCoverage.expected?.length > 0);
                return hasProblem;
            }
            if (filter === 'down') {
                return !(h.available === 1 || h.metrics?.agentPing === 1 || h.metrics?.icmpPing === 1);
            }
            return true;
        });
    }, [hosts, query, filter]);

    return (
        <Box>
            <Box sx={{
                display: 'flex',
                gap: 1,
                mb: 1.5,
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}>
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    border: '1px solid rgba(148,163,184,0.22)',
                    borderRadius: 999, px: 1.25, py: 0.25,
                    minWidth: { xs: '100%', sm: 260 },
                    flex: { xs: 1, sm: 'unset' },
                }}>
                    <Search size={14} />
                    <InputBase
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search hosts…"
                        sx={{ fontSize: 13, flex: 1 }}
                    />
                </Box>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={filter}
                    onChange={(_e, v) => v && setFilter(v)}
                    sx={{
                        '& .MuiToggleButton-root': {
                            textTransform: 'none',
                            fontSize: 12,
                            px: 1.5, py: 0.5,
                            borderColor: 'rgba(148,163,184,0.2)',
                        },
                    }}
                >
                    <ToggleButton value="all">All ({hosts.length})</ToggleButton>
                    <ToggleButton value="problems">Problems</ToggleButton>
                    <ToggleButton value="down">Down</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(auto-fill, minmax(260px, 1fr))',
                    md: 'repeat(auto-fill, minmax(280px, 1fr))',
                },
                gap: { xs: 1.5, sm: 2 },
            }}>
                {filtered.map(h => (
                    h.kind === 'ubiquiti'
                        ? <GatewayCard key={h.hostid} host={h} onClick={() => setSelected(h)} />
                        : <HostCard key={h.hostid} host={h} onClick={() => setSelected(h)} />
                ))}
            </Box>

            {filtered.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4, opacity: 0.55, fontSize: 13 }}>
                    {hosts.length === 0 ? 'No Zabbix hosts discovered.' : 'No hosts match the filters.'}
                </Box>
            )}

            <HostDetailDrawer
                open={Boolean(selected)}
                host={selected}
                onClose={() => setSelected(null)}
            />
        </Box>
    );
}
